// Formatters de display. Money em centavos (Decimal-safe — readme §19).
// NUNCA fazer math em float aqui — só apresentação.

const BRL = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

export function formatBRL(cents: number): string {
  if (!Number.isFinite(cents)) return BRL.format(0);
  return BRL.format(cents / 100);
}

// Custo de IA em dólares (RF-05). Recebe o valor JÁ em USD (não centavos) — os
// endpoints de custo devolvem Decimal→Number em dólares. Separado do BRL de
// propósito: custos por chamada costumam ser < US$0,01, então mostramos 2–4 casas.
const USD = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 4,
});

export function formatUsd(usd: number): string {
  if (!Number.isFinite(usd)) return USD.format(0);
  return USD.format(usd);
}

// Parse de input em reais BR → inteiro em centavos. Aceita "1.234,56",
// "R$ 1.234,56", "99", "99,90". Vírgula = separador decimal; pontos = milhar
// (descartados). Entrada vazia/inválida → 0. Só apresentação→domínio: arredonda
// pro centavo mais próximo, nunca trunca em float.
export function parseBRLToCents(input: string): number {
  const cleaned = input
    .replace(/[R$\s]/gi, '')
    .replace(/\./g, '')
    .replace(',', '.');
  if (cleaned === '') return 0;
  const reais = Number(cleaned);
  if (!Number.isFinite(reais)) return 0;
  return Math.round(reais * 100);
}

// Rótulo amigável p/ PII redigido. No soft-delete LGPD o telefone do contato vira
// o placeholder `anon:<id>` no back — nunca exibir isso cru na UI.
export const ANON_PII_LABEL = 'Dados removidos (LGPD)';

// E.164 → display BR amigável: +5511987654321 → (11) 98765-4321
export function formatPhone(phone: string): string {
  // Contato anonimizado (LGPD): telefone é o placeholder `anon:<id>`, não um número.
  if (phone.startsWith('anon:')) return ANON_PII_LABEL;
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 13 && digits.startsWith('55')) {
    const ddd = digits.slice(2, 4);
    const first = digits.slice(4, 9);
    const last = digits.slice(9);
    return `(${ddd}) ${first}-${last}`;
  }
  if (digits.length === 11) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  }
  return phone;
}

/**
 * Iniciais para o avatar — inclusive quando o "nome" é um telefone.
 *
 * 🔑 Contato sem profile name nasce com o PRÓPRIO NÚMERO no lugar do nome
 * (`inbound-ingest`: `name: name?.trim() || phone`). Tratado como palavra, o
 * avatar mostrava `55` em todos eles — o código do país, igual para o Brasil
 * inteiro, ou seja, zero informação. Com DDI 55 o que identifica é o DDD, os dois
 * dígitos seguintes.
 *
 * Número sem o 55 na frente (fixo antigo, importado) mantém os dois primeiros
 * dígitos, que ali já são o DDD.
 */
export function avatarInitials(name: string): string {
  const trimmed = name.trim();
  if (trimmed === '') return '?';
  // Só dígitos, com ou sem `+`/espaços/parênteses — é telefone, não nome.
  const digits = trimmed.replace(/[\s()+-]/g, '');
  if (/^\d{8,15}$/.test(digits)) {
    return digits.startsWith('55') && digits.length >= 12 ? digits.slice(2, 4) : digits.slice(0, 2);
  }
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return `${parts[0]![0] ?? ''}${parts[parts.length - 1]![0] ?? ''}`.toUpperCase();
}

// Máscara progressiva pra input de celular BR: (xx) x xxxx-xxxx (11 dígitos).
// Formata parcialmente conforme o usuário digita — usar no onChange de um campo
// controlado. Para enviar ao back, extraia só dígitos com .replace(/\D/g, '').
export function maskPhoneInputBR(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 11);
  if (digits.length === 0) return '';
  if (digits.length <= 2) return `(${digits}`;
  const ddd = digits.slice(0, 2);
  const rest = digits.slice(2);
  if (rest.length <= 1) return `(${ddd}) ${rest}`;
  const ninth = rest.slice(0, 1);
  const body = rest.slice(1);
  if (body.length <= 4) return `(${ddd}) ${ninth} ${body}`;
  return `(${ddd}) ${ninth} ${body.slice(0, 4)}-${body.slice(4)}`;
}
